// Auth context — single source of truth for the current user.
//
// Pen-test remediation: we no longer store the JWT, session_id, user role
// or any profile metadata in localStorage. Auth state lives in memory
// only, hydrated from `GET /auth/me` on app boot. The auth cookie
// (`noc_token`, HttpOnly) travels automatically with the request.
//
// Consumers:
//   const { user, role, isAuthenticated, isReady, refresh, signOut } = useAuth();
//
// Rules:
//   - `isReady === false` while /auth/me is in flight (initial boot).
//   - `isAuthenticated === false` when there is no active session.
//   - `role` is derived from `user.user_type` (admin | department | ngo).
//   - `user` shape matches whatever /auth/me returns (safe fields only).

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import API from '../api';

const AuthContext = createContext(null);

const mapRole = (userType) => {
  const t = String(userType || '').toUpperCase();
  if (t === 'ADMIN') return 'admin';
  if (t === 'DEPT_ADMIN') return 'dept_admin';
  if (t === 'DEPARTMENT') return 'department';
  if (t === 'STATE') return 'state';
  if (t === 'NGO' || t === 'OTHERS') return 'ngo';
  return t.toLowerCase();
};

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [isReady, setIsReady] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await API.get('/auth/me', { silent: true });
      const u = res?.data?.data?.user || res?.data?.data || null;
      setUser(u);
    } catch {
      setUser(null);
    } finally {
      setIsReady(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Called by LoginComponent after a successful POST /auth/login.
  const refresh = load;

  // Called by Header when the user clicks Logout.
  const signOut = useCallback(async () => {
    try { await API.post('/auth/logout'); } catch { /* ignore network errors */ }
    setUser(null);
    // Clear any legacy localStorage keys from previous deploys.
    ['token', 'accesstoken', 'uid', 'userInfo', 'userRole', 'userEmail',
     'isLoggedIn', 'loginResponse'].forEach((k) => localStorage.removeItem(k));
    window.location.replace('/login');
  }, []);

  const value = {
    user,
    role: mapRole(user?.user_type),
    isAuthenticated: !!user,
    isReady,
    refresh,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};

export default AuthContext;
